import {
    Button,
    DatePicker,
    EmptyState,
    ErrorState,
    Icon,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Spinner,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbChevronLeft, TbChevronRight, TbX } from "react-icons/tb";
import { toast } from "sonner";
import { z } from "zod";

import { TeamMemberSelector } from "@/components/ui/team-member-selector";
import { PERMISSIONS } from "@/constants/permissions";
import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { useUsersByIdsBatch } from "@/hooks/queries/user.queries";
import { BOOKING_STATUS, useBookings, useUpdateBooking } from "@/queries/booking.queries";
import { useEventTypes } from "@/queries/event-type.queries";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { getUserIdToLabelMap } from "@/utils/team.utils";

import { CalendarAppointmentsSelectionBar } from "./components/calendar-appointments-selection-bar";
import { CalendarAppointmentsTimeline } from "./components/calendar-appointments-timeline";
import { TransferAppointmentsDialog } from "./components/transfer-appointments-dialog";
import { buildCalendarAppointmentRows } from "./utils/calendar-appointments.utils";

const CalendarAppointmentsSearchSchema = z.object({
    eventTypeId: z.string().optional(),
    userId: z.string().optional(),
    date: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.CALENDAR_APPOINTMENTS)({
    component: CalendarAppointmentsPage,
    validateSearch: zodValidator(CalendarAppointmentsSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "appointment_management",
                to: APP_ROUTES_TO.CALENDAR_APPOINTMENTS,
            },
            title: "appointment_management",
        },
        permissions: [PERMISSIONS.CALL_SVC_TEAM_LEAD_BASE, PERMISSIONS.CALL_SVC_ADMIN_BASE],
    },
});

function CalendarAppointmentsPage() {
    return <CalendarAppointmentsContent />;
}

function CalendarAppointmentsContent() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const search = Route.useSearch({ structuralSharing: true });
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [transferOpen, setTransferOpen] = useState(false);
    const updateBooking = useUpdateBooking();

    const selectedDate = useMemo(() => {
        if (!search.date) {
            return DateTime.now().startOf("day");
        }

        const parsed = DateTime.fromISO(search.date);
        return parsed.isValid ? parsed.startOf("day") : DateTime.now().startOf("day");
    }, [search.date]);

    const dateKey = selectedDate.toFormat("yyyy-MM-dd");

    const updateSearch = useCallback(
        (updates: { eventTypeId?: string | null; userId?: string | null; date?: string }) => {
            navigate({
                to: Route.to,
                search: (prev) => {
                    const next = { ...prev };

                    if (updates.date !== undefined) {
                        next.date = updates.date;
                    }

                    if (updates.eventTypeId === null) {
                        delete next.eventTypeId;
                    } else if (updates.eventTypeId !== undefined) {
                        next.eventTypeId = updates.eventTypeId;
                        delete next.userId;
                    }

                    if (updates.userId === null) {
                        delete next.userId;
                    } else if (updates.userId !== undefined) {
                        next.userId = updates.userId;
                        delete next.eventTypeId;
                    }

                    return next;
                },
                replace: true,
            });
        },
        [navigate]
    );

    useEffect(() => {
        if (search.date) {
            return;
        }

        updateSearch({ date: DateTime.now().toFormat("yyyy-MM-dd") });
    }, [search.date, updateSearch]);

    const { data: eventTypes = [], isLoading: isLoadingEventTypes } = useEventTypes();

    const eventTypesById = useMemo(
        () => new Map(eventTypes.map((eventType) => [eventType.id, eventType])),
        [eventTypes]
    );

    const hasFilter = Boolean(search.eventTypeId || search.userId);

    const {
        data: appointments = [],
        isLoading: isLoadingAppointments,
        isError,
        error,
        refetch,
    } = useBookings(
        {
            dateFrom: dateKey,
            dateTo: dateKey,
            status: BOOKING_STATUS.CONFIRMED,
            ...(search.eventTypeId ? { eventTypeId: search.eventTypeId } : {}),
            ...(search.userId ? { hostIds: [search.userId] } : {}),
        },
        { enabled: hasFilter }
    );

    const hostIds = useMemo(() => {
        const ids = appointments
            .map((appointment) => appointment.hostId)
            .filter((hostId): hostId is string => Boolean(hostId));
        return [...new Set(ids)];
    }, [appointments]);

    const { data: teamsData } = useTeams();
    const { data: hostUsers = [] } = useUsersByIdsBatch(hostIds, hostIds.length > 0);

    const hostLabels = useMemo(() => {
        const labels = getUserIdToLabelMap(teamsData?.data ?? []);
        hostUsers.forEach((user) => {
            if (!labels.has(user.id)) {
                labels.set(user.id, user.name || user.email || user.id);
            }
        });
        return labels;
    }, [hostUsers, teamsData?.data]);

    const timelineRows = useMemo(
        () =>
            buildCalendarAppointmentRows({
                appointments,
                hostLabels,
            }),
        [appointments, hostLabels]
    );

    const visibleIds = useMemo(() => appointments.map((appointment) => appointment.id), [appointments]);

    useEffect(() => {
        setSelectedIds((current) => {
            const next = new Set([...current].filter((id) => visibleIds.includes(id)));
            return next.size === current.size ? current : next;
        });
    }, [visibleIds]);

    const handleDateChange = useCallback(
        (nextDate: DateTime) => {
            updateSearch({ date: nextDate.toFormat("yyyy-MM-dd") });
            setSelectedIds(new Set());
        },
        [updateSearch]
    );

    const handleToggleAppointment = useCallback((appointmentId: string) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(appointmentId)) {
                next.delete(appointmentId);
            } else {
                next.add(appointmentId);
            }
            return next;
        });
    }, []);

    const handleToggleRow = useCallback((appointmentIds: string[]) => {
        setSelectedIds((current) => {
            const allSelected = appointmentIds.every((id) => current.has(id));
            const next = new Set(current);

            appointmentIds.forEach((id) => {
                if (allSelected) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
            });

            return next;
        });
    }, []);

    const handleTransfer = useCallback(
        async (targetUserId: string | null) => {
            try {
                for (const id of selectedIds) {
                    await updateBooking.mutateAsync({ id, hostId: targetUserId });
                }
                toast.success(t(targetUserId ? "appointments_transferred" : "appointments_unassigned"));
                setSelectedIds(new Set());
                setTransferOpen(false);
            } catch {
                toast.error(t(targetUserId ? "failed_to_transfer_appointments" : "failed_to_unassign_appointments"));
            }
        },
        [selectedIds, t, updateBooking]
    );

    const isLoading = isLoadingEventTypes || (hasFilter && isLoadingAppointments);

    return (
        <div className="flex h-full w-full flex-col gap-4 overflow-auto p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    <Select
                        value={search.eventTypeId}
                        onValueChange={(eventTypeId) => updateSearch({ eventTypeId })}
                        disabled={isLoadingEventTypes || eventTypes.length === 0}
                    >
                        <SelectTrigger className="w-[220px]">
                            <SelectValue placeholder={t("select_event")} />
                        </SelectTrigger>
                        <SelectContent>
                            {eventTypes.map((eventType) => (
                                <SelectItem key={eventType.id} value={eventType.id}>
                                    <div className="flex items-center gap-2">
                                        {eventType.color ? (
                                            <span
                                                className="size-2.5 rounded-full"
                                                style={{ backgroundColor: eventType.color }}
                                            />
                                        ) : null}
                                        {eventType.title}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-9 w-9 p-0"
                        aria-label={t("clear_selection")}
                        disabled={!search.eventTypeId}
                        onClick={() => updateSearch({ eventTypeId: null })}
                    >
                        <Icon icon={TbX} size={16} />
                    </Button>

                    <div className="w-[280px]">
                        <TeamMemberSelector
                            selectedMemberId={search.userId}
                            onMemberChange={(userId) => updateSearch({ userId })}
                            labels={{
                                selectItem: "select_agent",
                                resetSelection: "all_agents",
                            }}
                        />
                    </div>

                    <div className="inline-flex items-center gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 p-0"
                            aria-label={t("previous_day")}
                            onClick={() => handleDateChange(selectedDate.minus({ days: 1 }))}
                        >
                            <Icon icon={TbChevronLeft} size={16} />
                        </Button>
                        <DatePicker
                            date={selectedDate.toJSDate()}
                            onDateChange={(date) => {
                                if (!date) {
                                    return;
                                }

                                handleDateChange(DateTime.fromJSDate(date));
                            }}
                        />
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 p-0"
                            aria-label={t("next_day")}
                            onClick={() => handleDateChange(selectedDate.plus({ days: 1 }))}
                        >
                            <Icon icon={TbChevronRight} size={16} />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={selectedDate.hasSame(DateTime.now(), "day")}
                            onClick={() => handleDateChange(DateTime.now())}
                        >
                            {t("today")}
                        </Button>
                    </div>
                </div>

                <CalendarAppointmentsSelectionBar
                    selectedCount={selectedIds.size}
                    totalCount={appointments.length}
                    isPending={updateBooking.isPending}
                    onSelectAll={() => setSelectedIds(new Set(visibleIds))}
                    onClear={() => setSelectedIds(new Set())}
                    onTransfer={() => setTransferOpen(true)}
                    onUnassign={() => {
                        void handleTransfer(null);
                    }}
                />
            </div>

            {!hasFilter && !isLoadingEventTypes ? <EmptyState variant="simple" title="select_event_or_agent" /> : null}

            {isLoading ? (
                <div className="flex flex-1 items-center justify-center py-16">
                    <Spinner />
                </div>
            ) : null}

            {isError ? (
                <ErrorState
                    title={t("error")}
                    message={error instanceof Error ? error.message : t("connection_error")}
                    onRetry={() => {
                        void refetch();
                    }}
                    retryLabel={t("try_again")}
                />
            ) : null}

            {hasFilter && !isLoading && !isError && appointments.length === 0 ? (
                <EmptyState variant="simple" title="no_appointments_for_filters" />
            ) : null}

            {hasFilter && !isLoading && !isError && appointments.length > 0 ? (
                <CalendarAppointmentsTimeline
                    rows={timelineRows}
                    eventTypesById={eventTypesById}
                    selectedIds={selectedIds}
                    onToggleAppointment={handleToggleAppointment}
                    onToggleRow={handleToggleRow}
                />
            ) : null}

            <TransferAppointmentsDialog
                open={transferOpen}
                selectedCount={selectedIds.size}
                isPending={updateBooking.isPending}
                onOpenChange={setTransferOpen}
                onConfirm={(userId) => {
                    void handleTransfer(userId);
                }}
            />
        </div>
    );
}
